import { Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material";
import useDBO from "../../hooks/useDBO";
import Loca from "../../components/Loca";
import Time from "../../components/Time";
import MonthChoice from "../../components/MonthChoice";
import HtClass from "../../components/HtClass";
import { LanguageContext } from "../../../../Context";
import Lang from "../../components/Lang";
import { weekdays } from "../../../../enums";
import _ from "lodash";
import { useContext, useState } from "react";
import Loading from "../../Loading";

export default function UserShifts({ user, open, setOpen }) {
  const { language } = useContext(LanguageContext);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth());
  const shifts = useDBO(`/my-shifts/${user._id}/${year}/${month}`);

  // Group shifts by date and sort descending
  const groupedShifts = _.groupBy(shifts.get || [], (s) => s.tDate);
  const sortedDates = Object.keys(groupedShifts).sort((a, b) => new Date(b) - new Date(a));

  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      fullWidth
      maxWidth="md"
      PaperProps={{
        style: {
          background: "#ffffff",
          borderRadius: "24px",
          boxShadow: "0 25px 60px rgba(0, 0, 0, 0.12)",
          color: "#0f172a",
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle className="flex justify-between items-center bg-slate-50 border-b border-slate-100 p-6">
        <div className="flex items-center gap-4">
          <div 
            style={{ 
              background: '#f0fdfa',
              padding: '10px',
              borderRadius: '14px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'inset 0 0 0 1px rgba(13, 148, 136, 0.1)'
            }}
          >
            <span style={{ fontSize: '1.5rem' }}>⏳</span>
          </div>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1.25rem', color: '#0f172a', letterSpacing: '-0.02em' }}>
               <Lang w="shiftHistory" />
            </div>
            <div className="flex items-center gap-2 mt-0.5">
              <span className="text-[0.65rem] font-bold text-slate-400 uppercase tracking-widest">
                {shifts.get?.length || 0} <Lang w="records" />
              </span>
              <div className="w-1 h-1 bg-slate-200 rounded-full"></div>
              <span className="text-[0.65rem] font-black text-teal-600 uppercase tracking-widest">
                {shifts.get ? shifts.reduce("hours") : 0} <Lang w="totalHours" />
              </span>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-2 rounded-xl border border-slate-200 shadow-sm">
           <MonthChoice
             year={year}
             setYear={setYear}
             month={month}
             setMonth={setMonth}
           />
        </div>
      </DialogTitle>

      <DialogContent className="p-0 relative">
        {shifts.loading && (
          <div className="absolute inset-0 z-50 bg-white/60 backdrop-blur-[1px] flex items-center justify-center">
            <Loading />
          </div>
        )}
        <div className={`max-h-[600px] overflow-y-auto transition-opacity duration-300 ${shifts.loading ? 'opacity-40' : 'opacity-100'}`}>
          {!shifts.get || shifts.get.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-slate-400 bg-white">
              <span className="text-5xl mb-4 opacity-20">☕</span>
              <p className="font-bold text-slate-500"><Lang w="noShiftsMonth" /></p>
            </div>
          ) : (
            <div className="bg-white">
              {sortedDates.map((date) => (
                <div key={date} className="relative">
                  {/* Sticky Date Header */}
                  <div className="sticky top-0 z-10 bg-slate-50/95 backdrop-blur-sm px-8 py-3.5 border-b border-slate-100 flex justify-between items-center">
                    <div className="flex items-baseline gap-2">
                      <span className="text-3xl font-black text-slate-800 tracking-tighter">
                        {getOrdinal(new Date(date).getDate())}
                      </span>
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-[0.2em]">
                        {weekdays[new Date(date).getDay()][language]}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                       <div className="w-2 h-2 rounded-full bg-slate-200 shadow-inner"></div>
                       <div className="text-[0.7rem] font-black text-slate-500 uppercase tracking-wider">
                         {groupedShifts[date].reduce((acc, s) => acc + (parseFloat(s.hours) || 0), 0)}h <Lang w="combined" />
                       </div>
                    </div>
                  </div>

                  {/* Daily Shifts */}
                  <div className="px-4 py-2">
                    {groupedShifts[date].map((shift, i) => (
                      <DrawShift
                        key={shift._id}
                        shift={shift}
                        isLast={i === groupedShifts[date].length - 1}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>

      <DialogActions className="p-6 bg-slate-50/50 border-t border-slate-100">
        <button
          className="bg-slate-900 hover:bg-slate-800 text-white font-bold py-2.5 px-8 rounded-xl text-xs transition-all active:scale-95 shadow-lg shadow-slate-200 cursor-pointer"
          onClick={() => setOpen(false)}
        >
          <Lang w="close" />
        </button>
      </DialogActions>
    </Dialog>
  );
}

function DrawShift({ shift, isLast }) {
  return (
    <div className={`flex items-center px-4 py-4 hover:bg-slate-50/80 transition-colors rounded-2xl group ${!isLast ? 'mb-1' : ''}`}>
      <div style={{ flex: 2 }} className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-teal-50 flex items-center justify-center text-teal-600 text-xs shadow-sm border border-teal-100">
          📍
        </div>
        <span className="text-sm font-[600] text-slate-600">
          <Loca id={shift.location} />
        </span>
      </div>
      
      <div style={{ flex: 2.5 }} className="flex items-center gap-2">
        <div className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-sm group-hover:border-teal-200 group-hover:shadow-teal-100/50 transition-all">
          <span className="text-xs font-bold text-slate-700">
            <Time time={shift.startTime} />
          </span>
          <span className="text-slate-300 text-[0.6rem] font-bold">—</span>
          <span className="text-xs font-bold text-slate-700">
            <Time time={shift.endTime} />
          </span>
        </div>
      </div>

      <div style={{ flex: 1.5 }} className="flex flex-col items-center text-center">
         <span className="text-[0.65rem] font-bold text-slate-400 uppercase tracking-tighter mb-0.5"><Lang w="assignment" /></span>
         <span className="text-xs font-black text-slate-600 truncate max-w-[120px]">{shift.role || <Lang w="standard" />}</span>
         {shift.htClass && (
           <div className="mt-1 transform scale-90 origin-center">
             <HtClass htClass={shift.htClass} />
           </div>
         )}
      </div>
      
      <div style={{ flex: 1 }} className="text-right">
        <div className="inline-block bg-teal-600 text-white font-black text-xs px-3 py-1.5 rounded-lg shadow-sm">
          {shift.hours}h
        </div>
      </div>
    </div>
  );
}
